<template>
    <h2 class="register-form__title">{{ $t('external.sublet_register.title') }}</h2>

    <div
        v-if="userAlreadyExists.hasError"
        v-html="userAlreadyExists.message"
        class="user-exists"
    ></div>

    <Former
        :action="submit"
        :buttonLabel="$t('views.profile.btnSave')"
        :gdprOptions="gdprOptions"
        :agreeOptions="agreeOptions"
        @update:modelValue="formerChange"
    >
        <div class="former__row former__row--column">
            <div class="former__row__col">
                <BaseInput
                    v-model="ssn"
                    ref="ssnField"
                    :label="`${ $t('ssn') }*`"
                    :vMessage="v.getErrors('ssn')"
                    @blur="validateForm('ssn')"
                />
            </div>
            <div class="former__row__col">
                <BaseCheckbox
                    v-model="nonSwedishSSN"
                    :label="`${ $t('ssn_non_swedish') }`"
                />
            </div>
            <div class="former__row__col">
                <BaseInput
                    v-model="first_name"
                    :label="`${ $t('first_name') }*`"
                    :vMessage="v.getErrors('first_name')"
                    @blur="validateForm('first_name')"
                />
            </div>
            <div class="former__row__col">
                <BaseInput
                    v-model="last_name"
                    :label="`${ $t('last_name') }*`"
                    :vMessage="v.getErrors('last_name')"
                    @blur="validateForm('last_name')"
                />
            </div>
            <div class="former__row">
                <div class="former__row__col former__row__col--6">
                    <BaseInput
                        v-model="street_address"
                        :label="`${ $t('street_address') }*`"
                        :vMessage="v.getErrors('street_address')"
                        @blur="validateForm('street_address')"
                    />
                </div>
                <div class="former__row__col former__row__col--4">
                    <BaseInput
                        v-model="street_number"
                        :label="`${ $t('street_number') }`"
                    />
                </div>
            </div>
            <div class="former__row__col">
                <BaseInput
                    v-model="coaddress"
                    :label="$t('care_of')"
                />
            </div>
            <div class="former__row">
                <div class="former__row__col former__row__col--4">
                    <BaseInput
                        v-model="zip"
                        :label="`${ $t('zip_code') }*`"
                        :vMessage="v.getErrors('zip')"
                        @blur="validateForm('zip')"
                    />
                </div>
                <div class="former__row__col former__row__col--6">
                    <BaseInput
                        v-model="city"
                        :label="`${ $t('city') }*`"
                        :vMessage="v.getErrors('city')"
                        @blur="validateForm('city')"
                    />
                </div>
            </div>
            <div class="former__row__col">
                <form-checkbox
                    v-model="non_swedish_postal_code"
                    :label="$t('zip_code_non_swedish')"
                />
            </div>
            <div class="former__row__col">
                <BaseInput
                    v-model="email"
                    :label="`${ $t('email') }*`"
                    :vMessage="v.getErrors('email')"
                    @blur="validateEmail"
                />

                <span
                    v-if="v.getErrors('is_email_ok').length > 0"
                    class="input-item__error"
                    :style="{ marginTop: '-10px', marginBottom: '15px' }"
                >
                    <span class="input-item__error__msg">
                        {{ v.getErrors('is_email_ok')[0] }}
                    </span>
                </span>
            </div>
            <div class="former__row__col">
                <BaseInput
                    v-model="cellphone"
                    :label="`${ $t('phone') }*`"
                    :vMessage="v.getErrors('cellphone')"
                    @blur="validateForm('cellphone')"
                />
            </div>
            <div class="former__row__col">
                <BasePassword
                    v-model="password"
                    :label="`${ $t('password') }*`"
                    :vMessage="v.getErrors('password')"
                    @update:modelValue="validateForm('password')"
                />
            </div>
            <div class="former__row__col">
                <BasePassword
                    v-model="password_confirmation"
                    :label="`${ $t('password_again') }*`"
                    :vMessage="v.getErrors('password_again')"
                    @update:modelValue="validateForm('password_again')"
                />
            </div>
        </div>

        <div class="former__row former__row--column">
            <div class="former__row__col">
                <BaseCheckbox
                    v-model="toggleAnotherPerson"
                    :label="$t('views.profile.contact_person')"
                />
            </div>

            <div v-if="toggleAnotherPerson" class="former__row former__row--column">
                <div class="former__row__col">
                    <BaseInput
                        v-model="contactPerson.first_name"
                        :label="`${ $t('first_name') }*`"
                        :vMessage="v.getErrors('contact_person_first_name')"
                        @blur="validateForm('contact_person_first_name')"
                    />
                </div>
                <div class="former__row__col">
                    <BaseInput
                        v-model="contactPerson.last_name"
                        :label="`${ $t('last_name') }*`"
                        :vMessage="v.getErrors('contact_person_last_name')"
                        @blur="validateForm('contact_person_last_name')"
                    />
                </div>
                <div class="former__row__col">
                    <BaseInput
                        v-model="contactPerson.email"
                        :label="`${ $t('email') }*`"
                        :vMessage="v.getErrors('contact_person_email')"
                        @blur="validateForm('contact_person_email')"
                    />
                </div>
                <div class="former__row__col">
                    <BaseInput
                        v-model="contactPerson.cellphone"
                        :label="`${ $t('phone') }*`"
                        :vMessage="v.getErrors('contact_person_phone')"
                        @blur="validateForm('contact_person_phone')"
                    />
                </div>
            </div>
        </div>
    </Former>
</template>

<script lang="ts">
    import { defineComponent, reactive, ref, toRefs, watch }              from 'vue'
    import { useI18n }                                             from 'vue-i18n'
    import { IUserUpdatePayload }                                  from '@type/modules/user/userState'
    import { authenticateUser }                                    from '@services/auth/authDataService'
    import { registerNewUser, checkEmail }                         from '@services/user/userDataService'
    import { setCache, getCache, currentLang, resetContactPerson } from '@/helpers'
    import Former                                                  from '@components/form/Former.vue'
    import BaseInput                                               from '@components/form/BaseInput.vue'
    import BaseCheckbox                                            from '@components/form/BaseCheckbox.vue'
    import BasePassword                                            from '@components/form/BasePassword.vue'
    import suite                                                   from '@validations/registerLandlord'

    export default defineComponent({
        components: {
            Former,
            BaseInput,
            BaseCheckbox,
            BasePassword
        },
        setup() {
            const { t, locale } = useI18n()
            const v                 = ref<any>(suite.get())
            const isEmailOk         = ref<boolean>(true)
            const agreed            = ref<boolean>(false)
            const userAlreadyExists = ref<{ hasError: boolean, message: string}>({
                hasError: false,
                message: ''
            })
            const payload = reactive<IUserUpdatePayload>({
                ssn: '',
                email: '',
                first_name: '',
                last_name: '',
                coaddress: '',
                street_address: '',
                street_number: '',
                zip: '',
                city: '',
                non_swedish_postal_code: false,
                cellphone: '',
                password: '',
                password_confirmation: '',
                contact_person: null
            })

            const contactPerson = reactive({
                email: '',
                first_name: '',
                last_name: '',
                cellphone: ''
            })

            const gdprOptions = {
                pgf_one: t('external.sublet_register.gdpr_one'),
                pgf_two: t('external.sublet_register.gdpr_two'),
                policy: t('external.sublet_register.policy')
            }

            const agreeOptions = {
                label: t('external.sublet_register.approve'),
                value: false
            }

            const nonSwedishSSN = ref<boolean>(false)
            const toggleAnotherPerson = ref<boolean>(false)

            const validateForm = (field?: string) => {
                v.value = suite({
                    ssn: payload.ssn,
                    hasNonSwedishSSN: nonSwedishSSN.value,
                    first_name: payload.first_name,
                    last_name: payload.last_name,
                    street_address: payload.street_address,
                    street_number: payload.street_number,
                    zip: payload.zip,
                    city: payload.city,
                    email: payload.email,
                    is_email_ok: isEmailOk.value,
                    cellphone: payload.cellphone,
                    password: payload.password,
                    password_again: payload.password_confirmation,
                    hasContactPerson: toggleAnotherPerson.value,
                    contact_person_first_name: contactPerson.first_name,
                    contact_person_last_name: contactPerson.last_name,
                    contact_person_email: contactPerson.email,
                    contact_person_phone: contactPerson.cellphone,
                    language: locale
                }, field)

                return v.value.errorCount > 0
            }

            const validateEmail = async (event: Event) => {
                const email = (event.target as HTMLInputElement).value

                if (email.includes('@')) {
                    await checkEmail(email)
                        .then(res => isEmailOk.value = res.data.available)
                } else {
                    isEmailOk.value = true
                }

                validateForm('is_email_ok')
                validateForm('email')
            }

            const formerChange = (value: { agree: boolean }) => agreed.value = value.agree

            const submit = () => {
                const hasError = validateForm()

                if (!hasError && agreed.value) {
                    registerUser(payload).then(res => {
                        if (res.status === 'Error') {
                            const scrollT = document.querySelector('.register-form') as HTMLElement
                            const validateKey = Object.keys(res.data)

                            userAlreadyExists.value.hasError = true
                            // TODO: Do we need to have the message from back-end?
                            userAlreadyExists.value.message = res.message

                            if (validateKey.includes('email')) {
                                userAlreadyExists.value.message = t('external.sublet_register.email_taken')
                            }

                            window.scrollTo(0, scrollT ? (scrollT.offsetTop - 71) : 0)
                        }

                        if (res.status === 'Success') {
                            authenticateUser().then((response) => {
                                const cache = getCache()
                                // Append user to cache
                                cache.user = response.data
                                // Append user role
                                cache.user.user_role = cache.user.roles[0]
                                // Check contact person
                                cache.user.contact_person = resetContactPerson(cache.user.contact_person)
                                // Set cache
                                setCache(cache)

                                location.href = `${ location.origin }/${ currentLang }/my-pages/new-estate/1`
                            })
                        }
                    })
                } else if (hasError) {
                    setTimeout(() => {
                        document.getElementsByClassName('input-item__error')[0].scrollIntoView();
                    }, 200);
                }
            }

            const registerUser = async (payload: IUserUpdatePayload) => {
                const result = await registerNewUser(payload).then(response => {
                    if (response.status === 'Success' && 'access_token' in response.data) {
                        // Save to cache
                        setCache(response.data)
                        return response
                    }
                }).catch(err => {
                    if (err.status === 'Error') {
                        return err
                    }
                })

                return result
            }

            watch(nonSwedishSSN, (value) => {
                validateForm('ssn')
            })

            watch(toggleAnotherPerson, (toggled) => {
                payload.contact_person = toggled ? contactPerson : null
            });

            return {
                v,
                toggleAnotherPerson,
                nonSwedishSSN,
                agreed,
                userAlreadyExists,
                ...toRefs(payload),
                contactPerson,
                gdprOptions,
                agreeOptions,
                formerChange,
                validateForm,
                validateEmail,
                submit
            }
        }
    })
</script>
